import { z } from "zod";
import { isoDate } from "./dates";
import { ALLOCATION_KEYS } from "./leases";

export const EXPENSE_CLASSIFICATIONS = ["UNDECIDED", "OPEX", "CAPEX", "TENANT_RECHARGE"] as const;
export type ExpenseClassificationCode = (typeof EXPENSE_CLASSIFICATIONS)[number];
export const CLASSIFICATION_LABEL: Record<ExpenseClassificationCode, string> = {
  UNDECIDED: "Nincs döntés",
  OPEX: "Üzemeltetés (OPEX)",
  CAPEX: "Beruházás (CAPEX)",
  TENANT_RECHARGE: "Továbbszámlázás bérlőnek",
};
export const EXPENSE_SCOPES = ["PROPERTY", "BUILDING", "FLOOR", "SPACES"] as const;
export const EXPENSE_SCOPE_LABEL = { PROPERTY: "Egész ingatlan", BUILDING: "Épület", FLOOR: "Szint", SPACES: "Helyiségek" } as const;

export const BUCKET_LABEL: Record<string, string> = {
  TENANT_RECOVERABLE: "Bérlőre áthárítva",
  TENANT_FLAT: "Bérlőre eső, átalány fedezi",
  DIRECT: "Közvetlen továbbszámlázás",
  NON_RECOVERABLE: "Nem áthárítható (leakage)",
  VACANCY: "Üresedési költség",
  OWNER_OCCUPIED: "Saját használat",
  EXCLUDED: "Elosztásból kizárt",
  CAPEX: "Beruházás (bérbeadó)",
  UNCLASSIFIED: "Minősítésre vár",
};

const optStr = (max = 200) => z.string().trim().max(max).nullable().optional();
const money = z.coerce.number({ invalid_type_error: "Számot adj meg" }).min(-1e12).max(1e12);

export const supplierSchema = z.object({
  name: z.string().trim().min(2).max(200),
  taxNumber: optStr(30),
  zip: optStr(12),
  city: optStr(120),
  street: optStr(200),
  email: z.string().trim().toLowerCase().email().nullable().optional().or(z.literal("")),
  phone: optStr(40),
  note: optStr(1000),
});

export const expenseSchema = z
  .object({
    legalEntityId: z.string().uuid().nullable().optional(),
    supplierId: z.string().uuid().nullable().optional(),
    costCategoryId: z.string().uuid({ message: "Válassz kategóriát" }),
    invoiceNo: optStr(80),
    invoiceDate: isoDate.nullable().optional(),
    periodFrom: isoDate,
    periodTo: isoDate,
    description: optStr(1000),
    net: money,
    vat: money,
    currency: z.string().trim().toUpperCase().length(3).default("HUF"),
    fxRate: z.coerce.number().positive().default(1),
    classification: z.enum(EXPENSE_CLASSIFICATIONS).default("UNDECIDED"),
    classificationNote: optStr(1000),
    rechargeLeaseId: z.string().uuid().nullable().optional(),
    /** Főmérő, amelyre a közüzemi számla szól – az almérős bérlők része nem kerül a területi elosztásba. */
    meterId: z.string().uuid().nullable().optional(),
    scope: z.enum(EXPENSE_SCOPES).default("PROPERTY"),
    scopeRefs: z.array(z.string().uuid()).default([]),
    allocationKeyOverride: z.enum(ALLOCATION_KEYS).nullable().optional(),
  })
  .refine((v) => v.periodTo >= v.periodFrom, { path: ["periodTo"], message: "A vége nem lehet a kezdete előtt" })
  .refine((v) => v.classification !== "TENANT_RECHARGE" || !!v.rechargeLeaseId, { path: ["rechargeLeaseId"], message: "Válaszd ki, kinek számlázzuk tovább" })
  .refine((v) => v.scope === "PROPERTY" || v.scopeRefs.length > 0, { path: ["scopeRefs"], message: "Válassz legalább egyet" });
export type ExpenseInput = z.infer<typeof expenseSchema>;

export const allocationQuerySchema = z.object({ from: isoDate, to: isoDate });
